/**
 * Sentry initialization for the frontend app.
 *
 * Mirrors the backend pattern in `apps/backend/src/core/sentry.py`: when
 * `EXPO_PUBLIC_SENTRY_DSN` is empty, the SDK is not initialized and Sentry
 * stays inert. This lets local/dev builds run without any Sentry config.
 *
 * Wiring lives in `apps/frontend/app/_layout.tsx` so init runs once at app
 * boot before any provider mounts. `wrapApp` is the SDK's HOC that captures
 * unhandled rendering errors; it is a no-op when Sentry is not initialized.
 */
import * as Sentry from "@sentry/react-native";

const DSN = (process.env.EXPO_PUBLIC_SENTRY_DSN ?? "").trim();
const APP_ENV = (process.env.EXPO_PUBLIC_APP_ENV ?? "development").trim();
const RELEASE = (process.env.EXPO_PUBLIC_SENTRY_RELEASE ?? "").trim();

let initialized = false;

export function initSentry(): void {
  if (initialized) return;
  if (!DSN) {
    // Disabled by configuration — mirror backend behaviour.
    return;
  }

  Sentry.init({
    dsn: DSN,
    environment: APP_ENV,
    release: RELEASE || undefined,
    // Errors only by default. Traces / profiles can be opted in by setting
    // EXPO_PUBLIC_SENTRY_TRACES_SAMPLE_RATE in Infisical when needed.
    tracesSampleRate: parseFloatOr(process.env.EXPO_PUBLIC_SENTRY_TRACES_SAMPLE_RATE, 0),
    sendDefaultPii: false,
  });

  initialized = true;
}

export const wrapApp = Sentry.wrap;

export function isSentryEnabled(): boolean {
  return initialized;
}

function parseFloatOr(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
}
